import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function SummaryGraph({ summaryData, field, form, subForm, line, section, shift }) {
  const colors = [
    "rgba(75, 192, 192, 1)",
    "rgba(153, 102, 255, 1)",
    "rgba(255, 159, 64, 1)",
    "rgba(255, 99, 132, 1)",
    "rgba(54, 162, 235, 1)",
    "rgba(255, 206, 86, 1)",
    "rgba(201, 203, 207, 1)",
  ];

  const chartData = () => {
    if (!field) return { datasets: [] };

    // Filter data based on selected filters
    let filteredData = summaryData.filter((d) => d.field === field);
    if (form) filteredData = filteredData.filter((d) => d.form === form);
    if (subForm) filteredData = filteredData.filter((d) => d.subform === subForm);
    if (section) filteredData = filteredData.filter((d) => d.Section === section);
    if (shift) filteredData = filteredData.filter((d) => d.shift === shift);

    // Get unique lines (if none selected, include all lines)
    const getLineValue = (item) => item.line ?? item.Line ?? "";
    const uniqueLines = line && line.length > 0 ? line : [...new Set(filteredData.map(getLineValue))];
    
    // Sort by date
    filteredData.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Get unique dates
    const dates = [...new Set(filteredData.map((d) => d.date))];

    const datasets = [];

    // Max and Min Limits (assume consistent across lines for the field, take first or max/min)
    const maxLimitData = dates.map((date) => {
      const items = filteredData.filter((d) => d.date === date);
      return items.length > 0 ? Math.max(...items.map((i) => i.max_limit)) : null;
    });
    datasets.push({
      label: "UCL",
      data: maxLimitData,
      borderColor: "rgba(255, 99, 132, 0.7)",
      backgroundColor: "rgba(255, 99, 132, 0.2)",
      fill: false,
      tension: 0.1,
      borderDash: [5, 5],
      pointRadius: 0,
    });

    const minLimitData = dates.map((date) => {
      const items = filteredData.filter((d) => d.date === date);
      return items.length > 0 ? Math.min(...items.map((i) => i.min_limit)) : null;
    });
    datasets.push({
      label: "LCL",
      data: minLimitData,
      borderColor: "rgba(54, 162, 235, 0.7)",
      backgroundColor: "rgba(54, 162, 235, 0.2)",
      fill: false,
      tension: 0.1,
      borderDash: [5, 5],
      pointRadius: 0,
    });

    // Observation Value datasets (one per line)
    if (uniqueLines.length === 0 || uniqueLines[0] === "") {
      // No lines available or no line filter, plot single average observation
      const observationData = dates.map((date) => {
        const items = filteredData.filter((d) => d.date === date);
        if (items.length === 0) return null;
        const sum = items.reduce((acc, i) => acc + (i.observation_value || 0), 0);
        return sum / items.length;
      });
      datasets.push({
        label: "Observation Value (Average)",
        data: observationData,
        borderColor: colors[0],
        backgroundColor: `${colors[0].replace("1)", "0.2)")}`,
        fill: true,
        tension: 0.3,
        pointBackgroundColor: observationData.map((value, index) =>
          value !== null &&
          maxLimitData[index] !== null &&
          minLimitData[index] !== null &&
          (value > maxLimitData[index] || value < minLimitData[index])
            ? "rgba(255, 0, 0, 1)"
            : colors[0]
        ),
        pointRadius: 5,
      });
    } else {
      // Plot one observation line per selected line
      uniqueLines.forEach((ln, index) => {
        const observationData = dates.map((date) => {
          const item = filteredData.find(
            (d) => d.date === date && getLineValue(d) === ln
          );
          return item ? item.observation_value : null;
        });
        datasets.push({
          label: `Observation Value - ${ln}`,
          data: observationData,
          borderColor: colors[index % colors.length],
          backgroundColor: `${colors[index % colors.length].replace("1)", "0.2)")}`,
          fill: true,
          tension: 0.3,
          pointBackgroundColor: observationData.map((value, index) =>
            value !== null &&
            maxLimitData[index] !== null &&
            minLimitData[index] !== null &&
            (value > maxLimitData[index] || value < minLimitData[index])
              ? "rgba(255, 0, 0, 1)"
              : colors[index % colors.length]
          ),
          pointRadius: 5,
        });
      });
    }

    return {
      labels: dates,
      datasets,
    };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { boxWidth: 20, padding: 20 },
      },
      title: {
        display: true,
        text: `Graphical Summary for ${field || "Field"}`,
        font: { size: 18, weight: "bold" },
        padding: 20,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y ?? "N/A"}`,
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      x: {
        title: { display: true, text: "Date", font: { size: 14 } },
        grid: { display: false },
      },
      y: {
        title: { display: true, text: "Value", font: { size: 14 } },
        grid: { color: "rgba(0, 0, 0, 0.1)" },
      },
    },
    animation: {
      duration: 1000,
      easing: "easeOutCubic",
    },
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg w-full h-[500px] transition-all duration-300 hover:shadow-xl">
      {field ? (
        <Line data={chartData()} options={options} />
      ) : (
        <p className="text-center text-gray-500 text-lg">
          Please select a field to display the graph
        </p>
      )}
    </div>
  );
}